import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/context/useAuth';
import {
	Users,
	ScanLine,
	BarChart3,
	List,
	Clock,
	ArrowLeftRight,
	Settings,
	ChevronLeft,
	ChevronRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTranslation } from '../../i18n';
import type { MerchantShellProfile, NavItem } from '@/types/common.types';

interface NavigationItem extends Omit<NavItem, 'name'> {
	nameKey: string;
}

// Navigation items for merchant users
// Note: Merchant roles (admin, staff) are in user_merchants table
// All items shown to authenticated users; server-side authorization handles access control
const navigationItems: NavigationItem[] = [
	{
		nameKey: 'sidebar.merchant.dashboard',
		href: '/merchant/dashboard',
		icon: BarChart3,
		roles: ['merchant'], // All merchant users
	},
	{
		nameKey: 'sidebar.merchant.couponList',
		href: '/merchant/coupon-list',
		icon: List,
		roles: ['merchant'],
	},
	{
		nameKey: 'sidebar.merchant.memberList',
		href: '/merchant/member-list',
		icon: Users,
		roles: ['merchant'],
	},
	{
		nameKey: 'sidebar.merchant.redemption',
		href: '/merchant/redemption',
		icon: ScanLine,
		roles: ['merchant'],
	},
	{
		nameKey: 'sidebar.merchant.redemptionHistory',
		href: '/merchant/redemption-history',
		icon: Clock,
		roles: ['merchant'],
	},
	{
		nameKey: 'sidebar.merchant.transactions',
		href: '/merchant/transactions',
		icon: ArrowLeftRight,
		roles: ['merchant'],
	},
	{
		nameKey: 'sidebar.merchant.settings',
		href: '/merchant/settings',
		icon: Settings,
		roles: ['merchant'],
	},
];

interface SidebarProps {
	isMobileOpen?: boolean;
	onMobileClose?: () => void;
	/** Merchant shell profile for role-based navigation. */
	profile?: MerchantShellProfile | null;
}

// Custom hook to detect viewport size
const useViewportSize = () => {
	const [viewport, setViewport] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

	useEffect(() => {
		const checkViewport = () => {
			const width = window.innerWidth;
			if (width < 768) {
				setViewport('mobile');
			} else if (width < 1024) {
				setViewport('tablet');
			} else {
				setViewport('desktop');
			}
		};

		checkViewport();
		window.addEventListener('resize', checkViewport);
		return () => window.removeEventListener('resize', checkViewport);
	}, []);

	return viewport;
};

export const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen = false, onMobileClose }) => {
	const { t } = useTranslation();
	const { session } = useAuth();
	const location = useLocation();
	const viewport = useViewportSize();

	// Collapsed state for tablet - persisted to localStorage
	const [isCollapsed, setIsCollapsed] = useState(() => {
		if (typeof window !== 'undefined') {
			const saved = localStorage.getItem('sidebarCollapsed');
			return saved === 'true';
		}
		return true; // Default collapsed on tablet
	});

	// Hover state for collapsed sidebar
	const [isHovered, setIsHovered] = useState(false);

	// Persist collapsed state to localStorage
	useEffect(() => {
		localStorage.setItem('sidebarCollapsed', String(isCollapsed));
	}, [isCollapsed]);

	// Filter navigation items - on web, all authenticated users are merchants
	// Server-side authorization handles actual access control via requireMerchant middleware
	const availableItems = session ? navigationItems.filter(item => item.roles.includes('merchant')) : [];

	const isActive = (href: string) => {
		if (href === '/merchant/dashboard') {
			return location.pathname === href;
		}
		// Special case for redemption to avoid matching redemption-history
		if (href === '/merchant/redemption') {
			return location.pathname === href || location.pathname.startsWith(href + '/');
		}
		return location.pathname.startsWith(href);
	};

	const toggleCollapsed = () => {
		setIsCollapsed(!isCollapsed);
	};

	// Determine if sidebar should show icons only
	const isTablet = viewport === 'tablet';
	const showIconsOnly = isTablet && isCollapsed && !isHovered;

	// Sidebar width based on state
	const sidebarWidth = showIconsOnly ? 'w-16' : 'w-64';

	return (
		<>
			{/* Sidebar */}
			<aside
				className={cn(
					'fixed left-0 top-16 bottom-0 bg-white border-r border-gray-200 z-40 transform transition-all duration-300 shadow-lg',
					sidebarWidth,
					// Mobile: hidden by default, show when isMobileOpen
					// Tablet (md): always visible, collapsed by default
					// Desktop (lg+): always visible, full width
					viewport === 'mobile'
						? isMobileOpen
							? 'translate-x-0'
							: '-translate-x-full'
						: 'translate-x-0',
				)}
				onMouseEnter={() => isTablet && setIsHovered(true)}
				onMouseLeave={() => isTablet && setIsHovered(false)}
			>
				<div className='h-full overflow-y-auto relative'>
					{/* Collapse/Expand toggle for tablet */}
					{isTablet && (
						<button
							onClick={toggleCollapsed}
							className='absolute -right-3 top-4 bg-white border border-gray-200 rounded-full p-1 shadow-md z-50 hover:bg-gray-50 transition-colors'
							aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
						>
							{isCollapsed ? (
								<ChevronRight className='h-4 w-4 text-gray-600' />
							) : (
								<ChevronLeft className='h-4 w-4 text-gray-600' />
							)}
						</button>
					)}

					<nav className={cn('p-4 space-y-2', showIconsOnly && 'p-2')}>
						{/* Navigation Items */}
						{availableItems.map(item => {
							const isItemActive = isActive(item.href);

							return (
								<Link
									key={item.nameKey}
									to={item.href}
									onClick={() => {
										// Close mobile sidebar when clicking on nav item
										if (isMobileOpen && onMobileClose) {
											onMobileClose();
										}
									}}
									className={cn(
										'flex items-center text-sm font-medium rounded-xl transition-all duration-300',
										showIconsOnly ? 'px-3 py-3 justify-center' : 'px-3 py-3',
										isItemActive
											? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border border-blue-200 shadow-sm'
											: 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:-translate-y-0.5',
									)}
									title={showIconsOnly ? t(item.nameKey) : undefined}
								>
									<item.icon
										className={cn(
											'h-5 w-5 flex-shrink-0',
											showIconsOnly ? '' : 'mr-3',
											isItemActive ? 'text-blue-500' : 'text-gray-400',
										)}
									/>
									{!showIconsOnly && (
										<>
											<span className='truncate'>{t(item.nameKey)}</span>
											{item.badge && (
												<span className='ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center'>
													{item.badge}
												</span>
											)}
										</>
									)}
								</Link>
							);
						})}
					</nav>
				</div>
			</aside>
		</>
	);
};
